/**
 * AgentCore Adapter - AWS Bedrock AgentCore with streaming, JWT auth, and localStorage persistence
 */

import {
  BedrockAgentCoreClient,
  InvokeAgentRuntimeCommand
} from '@aws-sdk/client-bedrock-agentcore';
import messagePhaseManager, {
  MessagePhase,
  isContextOverflowError,
  phaseRendersLoader
} from '../utils/MessagePhaseManager';
import { listAgentSessions, listAgentSessionHistory } from '../../../api/agentSessions';
import pubSub from '../../../utils/pubsub';
import {
  getCurrentUserId,
  clearUserStorage,
  clearAllAgentStorage,
  loadStoredSessions,
  loadStoredMessages,
  saveStoredSessions,
  saveStoredMessages,
  validateCache
} from './adapterStorageUtils';

// Service layer imports
import AuthService from './services/AuthService';
import SessionManager from './services/SessionManager';
import MessageManager from './services/MessageManager';
import SearchPollCoordinator from './services/SearchPollCoordinator';
import ResponseHandler from './services/ResponseHandler';
import BackendSyncService from './services/BackendSyncService';
import CacheSyncStrategy from './services/CacheSyncStrategy';
import PayloadBuilder from './services/PayloadBuilder';
import CallbackManager from './services/CallbackManager';
import RetryHandler from './services/RetryHandler';
import SearchHandler from './services/SearchHandler';
import SessionCrudService from './services/SessionCrudService';
import HiddenMessageHandler from './services/HiddenMessageHandler';
import StreamingProcessor from './services/StreamingProcessor';
import { streamToAsyncIterable, parseChunk, isStreamable } from './services/StreamingService';
import { removeSearchTags, SEARCH_INITIATION_MARKER, isPollMessage, SEARCH_CONFIG } from './services/SearchWorkflowService';
import {
  parseEnrichmentTags,
  removeEnrichmentTags,
  isEnrichmentControlReply,
  hasRevealMarker
} from './services/EnrichmentTagService';
import RevealPollCoordinator from './services/RevealPollCoordinator';
import { ENRICHMENT_REPLY_PREFIX, REVEAL_MARKER } from '../../../utils/constants/agentEnrichment';
import { detectToolExecution } from './services/ToolDetectionService';

const ADAPTER_NAME = 'AGENTCORE';

class AgentCoreAdapter {
  constructor(config, store) {
    if (import.meta.env.DEV) console.log('🚀 AGENTCORE: Initializing with real AWS AgentCore integration');

    this.store = store;
    this.config = config;
    this.agentRuntimeArn = config.agentRuntimeArn;
    this.qualifier = config.qualifier || 'DEFAULT';

    // Initialize AuthService for JWT management (delegates token operations)
    this._authService = new AuthService(store?.authStore);

    // Initialize AWS client with dummy credentials (JWT middleware handles auth)
    this.client = new BedrockAgentCoreClient({
      region: config.region,
      credentials: async () => ({
        accessKeyId: 'jwt-auth',
        secretAccessKey: 'jwt-auth'
      })
    });

    // Add JWT authentication middleware from AuthService
    const { middleware, options } = this._authService.createJwtMiddleware();
    this.client.middlewareStack.add(middleware, options);

    // Remove AWS signing middleware to prevent conflicts
    try {
      this.client.middlewareStack.remove('awsAuthMiddleware');
      this.client.middlewareStack.remove('credentialsMiddleware');
    } catch {
      // Expected - middleware may not exist
    }

    // TD-1845: Cache user ID on construction for logout cleanup
    // (auth store may be reset before logout event is processed)
    this._cachedUserId = this._getCurrentUserId();

    // Phase 4: Component-based architecture - managers own their state
    const storageUtils = {
      loadStoredSessions,
      saveStoredSessions: (sessions, st, name) => saveStoredSessions(st, sessions, name),
      loadStoredMessages,
      saveStoredMessages: (messages, st, name) => saveStoredMessages(st, messages, name),
      getCurrentUserId,
      clearUserStorage
    };
    this._sessionManager = new SessionManager(store, storageUtils);
    this._messageManager = new MessageManager(store, storageUtils);

    // Load persisted data via managers
    this._sessionManager.load(this._transformLoadedSession.bind(this));
    this._messageManager.load(this._transformLoadedMessage.bind(this));

    // Initialize callback manager for event notifications
    this._callbackManager = new CallbackManager();

    // Initialize retry handler for AWS command execution
    this._retryHandler = new RetryHandler(this.client);

    // TD-2347: tracks the in-flight streaming turn per session ({ abortController,
    // aiMessage }) so stopMessage(sessionId) can cancel the request and finalize
    // the message as "Stopped by user".
    this._activeStreams = new Map();

    // TD-2383: sessions whose current turn the user stopped. A hidden search poll
    // already in flight when Stop is clicked has no AbortController and cannot be
    // cancelled; this guard keeps its late result hidden instead of rendering it
    // after the stop. Cleared when a genuinely new turn starts (sendMessage /
    // retryMessage) and on logout / destroy.
    this._stoppedSessions = new Set();

    // Initialize search handler for session search
    this._searchHandler = new SearchHandler({
      sessionManager: this._sessionManager,
      messageManager: this._messageManager
    });

    // Initialize search workflow coordinator
    // TD-1904: Coordinator now passes sessionId to callbacks
    this._searchCoordinator = new SearchPollCoordinator({
      onStateChange: (sessionId, state) => this._notifyStateChange(sessionId, state),
      sendHiddenMessage: (sessionId, content) => this._sendSearchPoll(sessionId, content),
      onSearchProgress: (sessionId, event) => this._notifySearchProgress(sessionId, event)
    });

    // TD-2293: bulk-reveal poll loop ([REVEAL_STARTED] → CHECK_REVEAL_RESULTS
    // every ~20s → stop on the render turn). Visibility of [BUSY] responses is
    // owned by the search coordinator; this one only owns the reveal schedule.
    this._revealCoordinator = new RevealPollCoordinator({
      sendHiddenMessage: (sessionId, content) => this._sendHiddenMessage(sessionId, content)
    });

    // Fix #5: Memoized inactive search state - single object reference for React optimization.
    // When currentSearchState returns this object, React.memo/useMemo will correctly
    // detect referential equality across renders.
    this._inactiveSearchState = Object.freeze({
      active: false,
      toolName: null,
      searchId: null,
      startTime: null,
      sessionId: null
    });

    // Initialize response handler for streaming processing
    this._responseHandler = new ResponseHandler({
      handleSearchTags: (content, sessionId, options) => {
        // TD-2442: a reveal-loop turn ([REVEAL_*]) is never a search event —
        // routing it through the search coordinator would let it complete (and
        // truncate) an active search. Skip the search coordinator for reveal
        // turns; the reveal coordinator handles them below.
        const result = hasRevealMarker(content)
          ? { isInternalOnly: false, reason: 'reveal_turn' }
          : this._searchCoordinator.handleSearchTags(content, sessionId, options);
        // The same funnel drives the reveal poll lifecycle (start on
        // [REVEAL_STARTED], reschedule on [REVEAL_BUSY], stop on [REVEAL_RESULT]).
        this._revealCoordinator?.handleRevealTags(content, sessionId);
        // A reveal-pending poll turn ([REVEAL_BUSY]) is internal chatter — hide
        // it or it surfaces as an empty bubble.
        if (!result.isInternalOnly && content?.includes?.(REVEAL_MARKER.BUSY)) {
          return { isInternalOnly: true, reason: 'reveal_busy' };
        }
        return result;
      },
      // Strip BOTH namespaces from displayed content. A search-only message
      // still collapses to SEARCH_INITIATION_MARKER (the enrichment strip is a
      // no-op on the marker). A mixed search+enrichment turn does NOT produce
      // the marker — it strips to '' and is hidden via isInternalOnly /
      // the empty-content guard in onText instead.
      removeSearchTags: (content) => removeEnrichmentTags(removeSearchTags(content)),
      // TD-2293: extract enrichment cards from the raw content into responseData.
      parseEnrichmentTags: (content) => parseEnrichmentTags(content),
      detectTool: (chunk) => detectToolExecution(chunk),
      onToolDetected: (toolInfo) => this._handleToolDetected(toolInfo)
    });

    // Initialize backend sync service for data transformations
    this._backendSync = new BackendSyncService();

    // Initialize payload builder for request construction
    this._payloadBuilder = new PayloadBuilder({ authService: this._authService });

    // Wrap listSessions with adapter context for logging and extensibility
    const listSessionsWithContext = async () => {
      if (import.meta.env.DEV) console.log('🔍 AGENTCORE: Fetching sessions from backend');
      try {
        const sessions = await listAgentSessions();
        if (import.meta.env.DEV) console.log(`✅ AGENTCORE: Fetched ${sessions.length} sessions`);
        return sessions;
      } catch (error) {
        console.error('❌ AGENTCORE: Failed to fetch sessions:', error);
        throw error;
      }
    };

    // Initialize cache sync strategy for session caching and background refresh
    this._cacheSyncStrategy = new CacheSyncStrategy({
      sessionManager: this._sessionManager,
      messageManager: this._messageManager,
      backendSync: this._backendSync,
      listSessions: listSessionsWithContext,
      // TD-2571: republish the settle event so React can observe the background refresh, which
      // otherwise repopulates the session map with nothing telling the UI to look again.
      onSessionsRefreshed: (event) => this._callbackManager.notifySessionsRefreshed(event)
    });

    // Initialize session CRUD service for optimistic updates with backend sync
    // Fix #18: backendApi functions use dynamic imports for code-splitting.
    // DEPENDENCY: '../../../api/agentSessions' module MUST exist for CRUD operations.
    // Fix #5: Wrap dynamic imports in try-catch with descriptive errors.
    this._sessionCrudService = new SessionCrudService({
      sessionManager: this._sessionManager,
      messageManager: this._messageManager,
      backendApi: {
        updateAgentSession: async (id, updates) => {
          try {
            const { updateAgentSession } = await import('../../../api/agentSessions');
            return updateAgentSession(id, updates);
          } catch (importError) {
            throw new Error('Failed to load agentSessions API for update: ' + importError.message);
          }
        },
        deleteAgentSession: async (id) => {
          try {
            const { deleteAgentSession } = await import('../../../api/agentSessions');
            return deleteAgentSession(id);
          } catch (importError) {
            throw new Error('Failed to load agentSessions API for delete: ' + importError.message);
          }
        }
      },
      onMutation: () => this._markRecentMutation()
    });

    // Initialize hidden message handler for search polling
    // Fix #17: Pass getCurrentUserId for authorization checks
    this._hiddenMessageHandler = new HiddenMessageHandler({
      sessionManager: this._sessionManager,
      messageManager: this._messageManager,
      onInvoke: (sessionId, content, aiMessage, isHidden) =>
        this._invokeAgentCoreStreaming(sessionId, content, aiMessage, isHidden),
      onError: (error, sessionId, content) => {
        // Fix #2: Track hidden message failures for monitoring
        console.error('Hidden message failed:', {
          sessionId,
          content: content?.substring(0, 50),
          error
        });
      }
    });

    // Initialize streaming processor for response stream handling
    this._streamingProcessor = new StreamingProcessor({
      streamingService: { streamToAsyncIterable, parseChunk },
      toolDetectionService: { detectToolExecution },
      responseHandler: this._responseHandler
    });

    // PHASE MANAGER INTEGRATION
    // Fix #2: Store unsubscribe functions to prevent memory leaks
    this._unsubscribers = [
      messagePhaseManager.onMessageUpdate(this._handlePhaseManagerUpdate.bind(this)),
      messagePhaseManager.onPhaseChange(this._handlePhaseChange.bind(this))
    ];

    if (import.meta.env.DEV) console.log(
      '📂 AGENTCORE: Loaded',
      this._sessionManager.count(),
      'sessions and',
      this._messageManager.countSessions(),
      'message sets'
    );

    // ✅ [TD-1703] PREVENTION: Validate cache on startup
    this._validateCache({ autoCleanup: true });

    // ✅ [TD-1845] SECURITY: Subscribe to logout event to clear user data
    this._logoutHandler = (payload) => {
      if (payload?.event === 'logout') {
        this._handleLogout();
      }
    };
    pubSub.subscribe(this._logoutHandler);

    if (import.meta.env.DEV) console.log('🎯 AGENTCORE: Using agent ARN:', this.agentRuntimeArn);
  }

  // ============================================================================
  // SECURITY: User Identity & Logout Handling (TD-1845)
  // ============================================================================

  /**
   * Get current user ID from auth store.
   * Delegated to shared utility for consistency across adapters.
   * @returns {string|null} Current user's ID or null if not authenticated
   */
  _getCurrentUserId() {
    return getCurrentUserId(this.store, ADAPTER_NAME);
  }

  /**
   * Handle logout event - clear all user data from localStorage and memory
   * TD-1845: Critical security fix to prevent data exposure after logout
   * Uses shared utilities for consistent behavior across adapters.
   */
  _handleLogout() {
    // TD-1845 Fix #1: Refresh cache before using fallback to handle re-login scenarios
    const freshUserId = this._getCurrentUserId();
    if (freshUserId) {
      this._cachedUserId = freshUserId;
    }

    const userId = this._cachedUserId;
    if (import.meta.env.DEV) console.log(`🔒 ${ADAPTER_NAME}: Handling logout, clearing user data for:`, userId);

    // Clear user-scoped localStorage keys using shared utility
    if (userId) {
      clearUserStorage(userId, ADAPTER_NAME);
    } else {
      // TD-1845 Fix #3: Defense-in-depth fallback
      clearAllAgentStorage(ADAPTER_NAME);
    }

    // Clear in-memory data via managers
    this._sessionManager.clear();
    this._messageManager.clear();
    this._stoppedSessions?.clear(); // TD-2383
    this._cachedUserId = null;

    if (import.meta.env.DEV) console.log(`🔒 ${ADAPTER_NAME}: Cleared in-memory sessions and messages`);
  }

  // ============================================================================
  // PERSISTENCE LAYER (Private) - Delegated to managers
  // ============================================================================

  /** Transform session on load - handles lastActivity migration */
  _transformLoadedSession(session) {
    if (session.lastActivity) {
      session.lastActivity = new Date(session.lastActivity);
    } else {
      session.lastActivity = session.createdAt || new Date();
    }
    return session;
  }

  /** Transform message on load - re-evaluates hidden state based on content */
  _transformLoadedMessage(msg) {
    const restoredMsg = { ...msg, timestamp: new Date(msg.timestamp) };
    const content = restoredMsg.content || '';

    if (restoredMsg.type === 'ai' || restoredMsg.type === 'agent') {
      // TD-2341: an in-progress turn (thinking / tool_executing) can never resume in a freshly
      // loaded session — left as-is its loading row would animate forever and (being a stale
      // visible tool) suppress the loader for later turns. Normalize it to a completed response
      // so it renders as a static, terminal message.
      if (
        restoredMsg.phase === MessagePhase.THINKING ||
        restoredMsg.phase === MessagePhase.TOOL_EXECUTING
      ) {
        restoredMsg.phase = MessagePhase.RESPONSE;
        restoredMsg.isComplete = true;
      }
      restoredMsg.hidden =
        content === SEARCH_INITIATION_MARKER ||
        content.includes('[BUSY]') ||
        content === 'Agent response completed.' ||
        content.startsWith('CHECK_SEARCH_RESULTS:');
      // TD-2293: an enrichment-card turn (reveal confirm / job progress /
      // discovery / options) must stay visible even when its stripped content
      // collapsed to the fallback — the card renders from message.enrichment
      // (persisted in the local cache).
      if (restoredMsg.enrichment && content === 'Agent response completed.') {
        restoredMsg.hidden = false;
      }
    } else if (restoredMsg.type === 'user') {
      // TD-2293: hide the enrichment control replies (CONFIRM_REVEAL:/
      // CANCEL_REVEAL:/CHECK_REVEAL_RESULTS:) alongside the search poll.
      restoredMsg.hidden =
        content.startsWith('CHECK_SEARCH_RESULTS:') || isEnrichmentControlReply(content);
    }
    return restoredMsg;
  }

  // ============================================================================
  // SESSION API (Public) - Same interface as SimpleMockAdapter
  // ============================================================================

  async createSession() {
    // ✅ [TD-1703] FIX #2: Single session ID design
    // Generate ONE UUID that will be sent to AWS and used as primary key
    // Previous bug: Generated TWO separate UUIDs (sessionId + runtimeSessionId)
    // which caused ID mismatch when backend returned sessions
    const runtimeSessionId = crypto.randomUUID(); // Will be sent to AWS Bedrock AgentCore
    const mcpSessionId = crypto.randomUUID(); // MCP session ID

    const now = new Date();
    const session = {
      id: runtimeSessionId, // ✅ Use same ID for both frontend and backend
      runtimeSessionId, // Same value as id
      mcpSessionId,
      createdAt: now,
      lastActivity: now,
      title: 'New Chat',
      _justCreated: true,
      _awaitingFirstMessage: true // ✅ Flag to update ID from AWS response if different
    };

    // Store session with runtimeSessionId as key
    this._sessionManager.set(runtimeSessionId, session);
    this._messageManager.setForSession(runtimeSessionId, []);

    // Persist immediately
    this._sessionManager.save();
    this._messageManager.save();

    if (import.meta.env.DEV) console.log('📝 AGENTCORE: Session created with unified ID:', runtimeSessionId);

    return {
      sessionId: runtimeSessionId, // ✅ Return same ID for both
      runtimeSessionId,
      mcpSessionId
    };
  }

  async connect(sessionId) {
    const session = this._sessionManager.getById(sessionId);
    if (!session) throw new Error(`Session not found: ${sessionId}`);

    // Skip history fetch for newly created sessions
    if (session._justCreated) {
      delete session._justCreated;
      this._sessionManager.save();
      return;
    }

    // Check if session already has messages loaded
    const existingMessages = this._messageManager.getForSession(sessionId);
    if (existingMessages.length > 0) {
      existingMessages.forEach((msg) => this._notifyMessage(sessionId, msg));
      return;
    }

    // Fetch conversation history from backend
    try {
      const historyData = await listAgentSessionHistory(sessionId);
      if (!historyData || historyData.length === 0) return;

      // Fix #4: Destructure to get both messages and errors
      const { messages: restoredMessages, errors } =
        this._backendSync.transformBackendMessages(historyData);

      // Log transformation errors for visibility (non-blocking)
      if (errors.length > 0 && import.meta.env.DEV) {
        console.warn(
          `⚠️ AGENTCORE: ${errors.length} messages failed to transform for session ${sessionId}`
        );
      }

      this._messageManager.setForSession(sessionId, restoredMessages);
      restoredMessages.forEach((msg) => this._notifyMessage(sessionId, msg));
    } catch (error) {
      console.error('❌ AGENTCORE: Failed to fetch conversation history:', error);
      this._notifyError({
        type: 'history_load_failed',
        sessionId,
        error: error.message,
        canRetry: true
      });
    }
  }

  async sendMessage(sessionId, content, options = {}) {
    if (import.meta.env.DEV) console.log('💬 AGENTCORE: Received message for session:', sessionId);

    // TD-2346: optional sourceMessageId stamps a rerun turn with the id of the
    // original message it was re-run from (powers the "Rerun of …" subtitle).
    const { sourceMessageId = null } = options;

    // Verify session exists
    if (!this._sessionManager.has(sessionId)) {
      throw new Error(`Session not found: ${sessionId}`);
    }

    // TD-2383: a new user turn clears any prior stop guard for this session.
    this._stoppedSessions.delete(sessionId);

    const session = this._sessionManager.getById(sessionId);
    const sessionMessages = this._messageManager.getForSession(sessionId);

    // 1. Add user message
    const userMessage = {
      id: crypto.randomUUID(),
      type: 'user',
      content,
      timestamp: new Date(),
      sourceMessageId
    };

    sessionMessages.push(userMessage);
    this._messageManager.setForSession(sessionId, sessionMessages);

    // Update session title with first message
    if (session.title === 'New Chat') {
      session.title = content.length > 50 ? content.substring(0, 50) + '...' : content;
    }

    // Update lastActivity timestamp when user sends message
    session.lastActivity = new Date();
    this._sessionManager.set(sessionId, session);
    this._sessionManager.save();

    // Notify user message
    this._notifyMessage(sessionId, userMessage);

    // 2. Create AI message in thinking phase
    const aiMessage = messagePhaseManager.createThinkingMessage(sessionId);
    sessionMessages.push(aiMessage);
    this._messageManager.setForSession(sessionId, sessionMessages);

    // Set agent to thinking state
    if (import.meta.env.DEV) console.log('🤔 AGENTCORE: Agent is now thinking...');
    this._notifyStateChange(sessionId, 'thinking');

    // 3. Call real AgentCore API with streaming support
    this._invokeAgentCoreStreaming(sessionId, content, aiMessage).catch((error) => {
      // TD-2347: user Stop during the request — already finalized as "Stopped by
      // user" in stopMessage(); don't overwrite with an error state.
      if (error?.name === 'AbortError') {
        return;
      }
      console.error('❌ AGENTCORE: Failed to get agent response:', error);

      // Transition to error phase with proper error handling
      messagePhaseManager.transitionToError(aiMessage, error, {
        canRetry: RetryHandler.isRetryableError(error)
      });

      // Update message in storage
      const currentMessages = this._messageManager.getForSession(sessionId);
      const messageIndex = currentMessages.findIndex((m) => m.id === aiMessage.id);
      if (messageIndex !== -1) {
        currentMessages[messageIndex] = aiMessage;
        this._messageManager.setForSession(sessionId, currentMessages);
      }

      this._notifyStateChange(sessionId, 'idle');
    });
  }

  /**
   * Stop the in-flight turn for a session (TD-2347).
   * Aborts the streaming request, halts any active search poll loop, finalizes the
   * in-progress message as "Stopped by user", and returns the session to idle.
   * Safe to call when nothing is running (guarded by isComplete + active checks).
   * @param {string} sessionId - Session whose current turn should be stopped
   */
  stopMessage(sessionId) {
    const entry = this._activeStreams.get(sessionId);

    // Capture BEFORE we stop anything: is a search genuinely still awaiting results?
    // isAwaitingResults excludes the ~300ms completion window where results have
    // already been surfaced (see SearchPollCoordinator), so we never append a
    // "Stopped by user" note on top of real results that just arrived.
    const awaitingResults = this._searchCoordinator?.isAwaitingResults(sessionId);

    // TD-2383: mark the session stopped so a hidden poll still in flight (which has
    // no AbortController) keeps its late result hidden instead of rendering it.
    this._stoppedSessions.add(sessionId);

    // 1. Abort the in-flight streaming request (visible turns only)
    if (entry?.abortController && !entry.abortController.signal.aborted) {
      entry.abortController.abort();
    }

    // 2. Halt the FE search poll loop for this session (no-op if not searching)
    this._searchCoordinator?.stopForSession(sessionId);
    // TD-2293: also halt a running bulk-reveal poll loop (no-op otherwise)
    this._revealCoordinator?.stopForSession(sessionId);

    // 3. Leave a visible "Stopped by user" acknowledgement via exactly one path,
    //    so the stop always lands somewhere and no loader animation survives:
    if (entry?.aiMessage && !entry.aiMessage.isComplete) {
      // (a) Tracked streaming / tool turn — stamp the in-flight message.
      messagePhaseManager.transitionToStopped(entry.aiMessage);
      this._updateMessageInStorage(sessionId, entry.aiMessage);
    } else if (awaitingResults) {
      // (b) Long search with no tracked stream (trigger ended; poll turns hidden)
      //     — synthesize a fresh visible acknowledgement.
      this._appendStoppedAcknowledgement(sessionId);
    } else {
      // (c) Fallback — finalize any lingering in-progress visible message so a
      //     per-message loader can never be left animating after the stop.
      this._finalizeLingeringLoader(sessionId);
    }

    this._activeStreams.delete(sessionId);

    // 4. Always return to idle so the input bar becomes usable immediately.
    this._notifyStateChange(sessionId, 'idle');
  }

  /**
   * Append a fresh visible "Stopped by user" message (TD-2383).
   * Used when the user stops a long search that has no tracked streaming turn (the
   * trigger stream already ended and poll turns are hidden). Building it from a
   * thinking message means transitionToStopped sees no meaningful partial content
   * and applies the clean "Stopped by user" label — never the internal
   * SEARCH_INITIATION marker the hidden trigger carries.
   * @param {string} sessionId - Session to acknowledge the stop in
   * @private
   */
  _appendStoppedAcknowledgement(sessionId) {
    const message = messagePhaseManager.createThinkingMessage(sessionId);
    messagePhaseManager.transitionToStopped(message);

    // add() is the canonical append (push + persist); the message already carries a
    // timestamp from createThinkingMessage, so add()'s back-fill is a no-op.
    this._messageManager.add(sessionId, message);
    this._notifyMessage(sessionId, message);
  }

  /**
   * Finalize the last visible in-progress AI message for a session as stopped
   * (TD-2383). Defensive fallback so a per-message loader (thinking / tool) can
   * never be left animating after a stop, even when the turn was never tracked in
   * _activeStreams. Scoped to the last visible message (mirrors the list-level
   * loader's own scoping) so an earlier interrupted turn isn't disturbed.
   * @param {string} sessionId - Session whose lingering loader should be finalized
   * @private
   */
  _finalizeLingeringLoader(sessionId) {
    // The loader always belongs to the LAST visible AI message (same scoping as the
    // list-level loader in AgentChatMessageList via phaseRendersLoader), so only that
    // one can be mid-animation; earlier interrupted turns are left untouched.
    const messages = this._messageManager.getForSession(sessionId);
    const last = [...messages]
      .reverse()
      .find((msg) => !msg.hidden && (msg.type === 'ai' || msg.type === 'agent'));

    if (!last || last.isComplete || !phaseRendersLoader(last.phase)) {
      return;
    }

    // Never let the internal marker leak into the visible label.
    if (last.content === SEARCH_INITIATION_MARKER) {
      last.content = '';
    }
    messagePhaseManager.transitionToStopped(last);
    this._updateMessageInStorage(sessionId, last);
  }

  /**
   * Get JWT token from current Cognito session.
   * Delegates to AuthService for token management and refresh.
   */
  getJwtToken = async () => {
    return this._authService.getJwtToken();
  };

  // ============================================================================
  // AGENTCORE INTEGRATION (Private)
  // ============================================================================

  async _invokeAgentCoreStreaming(sessionId, content, aiMessage, isHiddenMessage = false) {
    const session = this._sessionManager.getById(sessionId);

    // Only change state for non-hidden messages
    if (!isHiddenMessage) {
      // Set agent to responding state
      if (import.meta.env.DEV) console.log('🤖 AGENTCORE: Agent is now responding...');
      this._notifyStateChange(sessionId, 'responding');
    } else if (import.meta.env.DEV) {
      console.log('🔍 AGENTCORE: Processing hidden polling message (no state change)');
    }

    // Get JWT token and store it for middleware access
    const jwtToken = await this.getJwtToken();
    if (!jwtToken) {
      throw new Error('No JWT token available for AgentCore authentication');
    }

    // Store JWT token for AuthService middleware to use
    this._authService.setCurrentToken(jwtToken);

    // Build request payload via PayloadBuilder
    const { payload, userEmail } = this._payloadBuilder.buildPayload({
      content,
      jwtToken
    });
    if (import.meta.env.DEV) console.log('📤 AGENTCORE: Sending request for user:', userEmail);

    const input = {
      runtimeSessionId: session.runtimeSessionId,
      agentRuntimeArn: this.agentRuntimeArn,
      qualifier: this.qualifier,
      payload
    };

    const command = new InvokeAgentRuntimeCommand(input);

    // TD-2347: register an AbortController so the user can stop this turn. Hidden
    // poll turns are not individually stoppable here — they are halted via the
    // search coordinator (stopForSession) instead.
    const abortController = isHiddenMessage ? null : new AbortController();
    if (abortController) {
      this._activeStreams.set(sessionId, { abortController, aiMessage });
    }

    const response = await this._executeWithRetry(command, {
      abortSignal: abortController?.signal
    });

    // ✅ [TD-1703] FIX #2: Update session ID if AWS returns different one
    if (session._awaitingFirstMessage && response.sessionId) {
      const awsSessionId = response.sessionId;
      if (awsSessionId !== session.runtimeSessionId) {
        if (import.meta.env.DEV) console.log('⚠️ Session ID mismatch, updating to:', awsSessionId);
        await this._updateSessionId(sessionId, awsSessionId);
        sessionId = awsSessionId;
      } else {
        session._awaitingFirstMessage = false;
        this._sessionManager.set(sessionId, session);
        this._sessionManager.save();
      }
    }

    // Overflow error stash — closure variable to avoid leaking to localStorage.
    // Cleared by onComplete if Strands recovers, consumed by post-processing otherwise.
    let overflowErrorMessage = null;

    // TD-2952: correlate poll responses to the searchId in the outbound CHECK_SEARCH_RESULTS
    const pollSearchId = isPollMessage(content)
      ? content.slice(SEARCH_CONFIG.POLL_MESSAGE_PREFIX.length)
      : undefined;

    // Process streaming response via StreamingProcessor
    try {
      const result = await this._streamingProcessor.processStream(response.response, sessionId, {
        pollSearchId,
        // Shared: raw chunk monitoring
        onChunk: (chunk, count) => {
          if (import.meta.env.DEV) {
            console.log(
              `🔍 AGENTCORE: Processing chunk #${count}:`,
              chunk.substring(0, 200) + (chunk.length > 200 ? '...' : '')
            );
          }
        },

        // SSE: Agent is entering a new reasoning loop
        onThinking: () => {
          if (aiMessage.phase !== MessagePhase.THINKING) {
            messagePhaseManager.transitionToThinking(aiMessage);
            this._updateMessageInStorage(sessionId, aiMessage);
          }
        },

        // SSE: Incremental text content — apply search tag removal on full accumulated text
        onText: (textChunk, accumulatedText) => {
          if (aiMessage.phase !== MessagePhase.RESPONSE || aiMessage.isComplete) {
            messagePhaseManager.startStreamingResponse(aiMessage);
          }
          aiMessage.content = removeEnrichmentTags(removeSearchTags(accumulatedText));
          // TD-2341: a response that collapses to the SEARCH_INITIATION placeholder is internal-
          // only — hide it now so it never flickers as a visible bubble mid-stream (the
          // authoritative hidden flag is otherwise applied only at onComplete).
          // TD-2293: same for content that strips to '' (a tag-only turn, e.g. a
          // reveal marker or mixed tags) — it un-hides as soon as prose arrives.
          aiMessage.hidden =
            isHiddenMessage ||
            aiMessage.content === SEARCH_INITIATION_MARKER ||
            aiMessage.content === '';
          this._updateMessageInStorage(sessionId, aiMessage);
          // Skip UI notification for hidden messages — they'll notify once via
          // the hidden→visible path after onComplete if the response is visible
          if (!isHiddenMessage) {
            this._notifyMessage(sessionId, aiMessage);
          }
        },

        // SSE: Tool invocation started
        onToolStart: (tool, toolId) => {
          messagePhaseManager.transitionToToolExecution(aiMessage, tool, { toolId });
          this._updateMessageInStorage(sessionId, aiMessage);
        },

        // SSE: Tool invocation completed — phase transition handled by subsequent thinking/text
        onToolEnd: (tool, toolId) => {
          if (import.meta.env.DEV) {
            console.log(`🔧 AGENTCORE: Tool completed: ${tool} (${toolId})`);
          }
        },

        // SSE: Agent encountered an error
        // For overflow errors eligible for auto-retry, we stash the message and
        // handle it AFTER processStream returns (in post-processing below).
        // This prevents any error phase transition or UI render — the user only
        // sees continuous "thinking" during the silent retry.
        onError: (message) => {
          aiMessage.hidden = isHiddenMessage;
          if (isContextOverflowError(message)) {
            // Always stash overflow silently — even on retry attempts.
            // Strands may recover internally within this same stream.
            // Post-processing decides whether to frontend-retry or show error.
            overflowErrorMessage = message;
            return;
          }
          messagePhaseManager.transitionToError(aiMessage, new Error(message));
          this._updateMessageInStorage(sessionId, aiMessage);
        },

        // Legacy: tool detection from raw content (fallback when no SSE events)
        onToolDetected: ({ toolName, params }) => {
          messagePhaseManager.transitionToToolExecution(aiMessage, toolName, params);
          this._updateMessageInStorage(sessionId, aiMessage);
        },
        onToolResults: (toolResults) => {
          messagePhaseManager.transitionToToolResults(aiMessage, toolResults);
          this._updateMessageInStorage(sessionId, aiMessage);
        },

        // Shared: stream completion — finalize message
        onComplete: (responseData) => {
          // Set hidden flag BEFORE finalizing to prevent race condition
          // where UI renders the message before hidden flag is applied
          aiMessage.hidden = responseData.isInternalOnly;

          // TD-2293: attach parsed enrichment cards (options / quote / discovery /
          // result / job) so AgentAIMessage can render them. Null when none — a
          // hidden→visible control reply carries its reveal result here.
          aiMessage.enrichment = responseData.enrichment ?? null;

          // Strands recovered from overflow internally — clear stash to skip post-processing retry
          if (overflowErrorMessage) {
            overflowErrorMessage = null;
            if (import.meta.env.DEV) console.log('✅ AGENTCORE: Strands recovered from overflow internally');
          }

          if (aiMessage.phase === MessagePhase.RESPONSE && !aiMessage.isComplete) {
            // SSE path: streaming was active, finalize with clean content
            messagePhaseManager.finalizeStreamingResponse(aiMessage, responseData.content);
          } else {
            // Legacy path or edge case: direct transition
            messagePhaseManager.transitionToResponse(aiMessage, responseData.content);
          }
          this._updateMessageInStorage(sessionId, aiMessage);
        }
      }, abortController?.signal);

      // TD-2347: stream was aborted by the user — stopMessage() already finalized
      // the message; nothing more to do for this turn.
      if (result?.aborted) {
        return;
      }

      if (import.meta.env.DEV) console.log(`📊 AGENTCORE: ${result.chunkCount} chunks processed`);

      // Auto-retry on context overflow — stashed by onError, cleared by onComplete if Strands recovered.
      if (overflowErrorMessage) {
        const overflowMsg = overflowErrorMessage;
        overflowErrorMessage = null;

        if (!aiMessage._overflowRetried && aiMessage.phase !== MessagePhase.ERROR) {
          // First overflow — retry once with reduced context
          aiMessage._overflowRetried = true;
          if (import.meta.env.DEV) console.log('🔄 AGENTCORE: Context overflow — auto-retrying with reduced context');
          messagePhaseManager.transitionToThinking(aiMessage);
          this._updateMessageInStorage(sessionId, aiMessage);
          try {
            return await this._invokeAgentCoreStreaming(sessionId, content, aiMessage, isHiddenMessage);
          } catch {
            messagePhaseManager.transitionToError(aiMessage, new Error(overflowMsg));
            this._updateMessageInStorage(sessionId, aiMessage);
            return;
          }
        } else {
          // Already retried or in error phase — Strands didn't recover, show error
          messagePhaseManager.transitionToError(aiMessage, new Error(overflowMsg));
          this._updateMessageInStorage(sessionId, aiMessage);
          return;
        }
      }

      // Update lastActivity timestamp
      const updatedSession = this._sessionManager.getById(sessionId);
      if (updatedSession) {
        updatedSession.lastActivity = new Date();
        this._sessionManager.set(sessionId, updatedSession);
        this._sessionManager.save();
      }

      // Fix #26: Use aiMessage.hidden as single source of truth for visibility
      // Handle state transitions based on message visibility
      if (!isHiddenMessage) {
        if (!this._searchCoordinator.isSearchActiveForSession(sessionId)) this._notifyStateChange(sessionId, 'idle');
      } else if (!aiMessage.hidden) {
        // TD-2383: the user stopped this session while this poll was in flight —
        // discard the late result (keep it hidden) rather than rendering it after
        // the "Stopped by user" acknowledgement. Clear the guard as it fires so it
        // suppresses exactly this one in-flight poll, not every later result until
        // the next user turn.
        if (this._stoppedSessions.has(sessionId)) {
          this._stoppedSessions.delete(sessionId);
          aiMessage.hidden = true;
          this._messageManager.save();
          return;
        }
        // Originally hidden message but response analysis determined it should be visible
        // Force persist to storage before notifying UI to prevent race condition
        if (import.meta.env.DEV) {
          console.log('📢 AGENTCORE: Making hidden message visible:', {
            messageId: aiMessage.id,
            content: aiMessage.content?.substring(0, 50),
            reason: result.responseData?.reason
          });
        }
        this._messageManager.save();
        this._notifyMessage(sessionId, aiMessage);
        if (!this._searchCoordinator.isSearchActiveForSession(sessionId)) this._notifyStateChange(sessionId, 'idle');
      }
    } catch (streamError) {
      // TD-2347: a user Stop aborts the request/stream — stopMessage() has already
      // finalized the message as "Stopped by user"; swallow the abort so we don't
      // overwrite that with an error state.
      if (streamError?.name === 'AbortError' || abortController?.signal.aborted) {
        if (import.meta.env.DEV) console.log('⏹ AGENTCORE: stream aborted by user');
        return;
      }
      console.error('❌ AGENTCORE: Streaming error:', streamError);
      messagePhaseManager.transitionToError(aiMessage, streamError, {
        canRetry: RetryHandler.isRetryableError(streamError)
      });
      this._updateMessageInStorage(sessionId, aiMessage);
      if (!isHiddenMessage && !this._searchCoordinator.isSearchActiveForSession(sessionId))
        this._notifyStateChange(sessionId, 'idle');
      throw streamError;
    } finally {
      // TD-2347: release the tracked controller once the turn is no longer in-flight,
      // so completed turns don't pin their aiMessage in _activeStreams. Only visible
      // turns are tracked here; hidden poll turns are intentionally never registered
      // (they are halted via the search coordinator). The overflow-retry recursion
      // re-sets its own entry before this runs. (stopMessage/destroy also delete/clear.)
      if (!isHiddenMessage) {
        this._activeStreams.delete(sessionId);
      }
    }
  }

  /**
   * Execute AWS command with retry, then validate streaming response.
   * Fix #1: Delegates retry logic to RetryHandler, keeps streaming validation here.
   *
   * @param {Object} command - AWS SDK command
   * @param {number} [maxRetries=3] - Max retry attempts
   * @returns {Promise<Object>} Validated streaming response
   */
  async _executeWithRetry(command, options = {}) {
    const { maxRetries = 3, abortSignal } = options;
    // Delegate retry logic to RetryHandler (Fix #1)
    const response = await this._retryHandler.executeWithRetry(command, { maxRetries, abortSignal });

    // TD-1767: Safari-compatible validation using StreamingService
    // Accept streams with EITHER Symbol.asyncIterator OR getReader()
    if (!response?.response || !isStreamable(response.response)) {
      throw new Error('Invalid AgentCore response: missing streaming capability');
    }

    return response;
  }

  /**
   * Retry a failed message
   * @param {string} sessionId - Session containing the message
   * @param {string} messageId - ID of the failed message to retry
   * @returns {Promise<void>}
   */
  async retryMessage(sessionId, messageId) {
    if (import.meta.env.DEV) console.log('🔄 AGENTCORE: Retrying message:', messageId, 'in session:', sessionId);

    const session = this._sessionManager.getById(sessionId);
    if (!session) {
      throw new Error(`Session not found: ${sessionId}`);
    }

    // TD-2383: a retry is a fresh turn — clear any prior stop guard.
    this._stoppedSessions.delete(sessionId);

    const messages = this._messageManager.getForSession(sessionId);
    const failedMessage = messages.find((m) => m.id === messageId);
    if (!failedMessage) {
      throw new Error(`Message not found: ${messageId}`);
    }

    if (failedMessage.phase !== MessagePhase.ERROR) {
      if (import.meta.env.DEV) console.warn(
        '⚠️ AGENTCORE: Message is not in error state, cannot retry:',
        failedMessage.phase
      );
      return;
    }

    // Find the original user message that triggered this AI response
    const messageIndex = messages.findIndex((m) => m.id === messageId);
    const userMessage = messageIndex > 0 ? messages[messageIndex - 1] : null;

    if (!userMessage || userMessage.type !== 'user') {
      throw new Error('Cannot find original user message to retry');
    }

    if (import.meta.env.DEV) console.log('🔄 AGENTCORE: Found original user message:', userMessage.content);

    // Reset the failed AI message to thinking state
    messagePhaseManager.transitionToThinking(failedMessage);
    this._updateMessageInStorage(sessionId, failedMessage);
    this._notifyMessage(sessionId, failedMessage);

    // Re-attempt the agent invocation
    this._invokeAgentCoreStreaming(sessionId, userMessage.content, failedMessage).catch((error) => {
      console.error('❌ AGENTCORE: Retry failed:', error);
      messagePhaseManager.transitionToError(failedMessage, error, {
        canRetry: RetryHandler.isRetryableError(error)
      });
      this._updateMessageInStorage(sessionId, failedMessage);
      this._notifyMessage(sessionId, failedMessage);
    });
  }

  // ============================================================================
  // DATA ACCESS API (Public) - Same interface as SimpleMockAdapter
  // ============================================================================

  getSession(sessionId) {
    return this._sessionManager.getById(sessionId);
  }

  getMessages(sessionId) {
    const allMessages = this._messageManager.getForSession(sessionId);

    // Filter out hidden messages (used for internal polling)
    const visibleMessages = allMessages.filter((message) => !message.hidden);

    if (import.meta.env.DEV) console.log(
      '📋 AGENTCORE: getMessages called for session',
      sessionId,
      'returning',
      visibleMessages.length,
      'visible messages',
      '(filtered from',
      allMessages.length,
      'total)'
    );

    return visibleMessages;
  }

  /**
   * Get all sessions for current user.
   * Delegates to CacheSyncStrategy for cache-first retrieval.
   * Returns sessions sorted by updated_at (newest first).
   */
  async getAllSessions(options = {}) {
    return this._cacheSyncStrategy.getAllSessions(options);
  }

  /**
   * Refresh sessions from backend in background.
   * @deprecated Use _cacheSyncStrategy.refreshInBackground() directly
   * @returns {Promise<void>}
   */
  async _refreshSessionsInBackground() {
    return this._cacheSyncStrategy.refreshInBackground();
  }

  /**
   * Get recent sessions (limit by count)
   * Uses getAllSessions which fetches from backend
   */
  async getRecentSessions(limit = 10) {
    const allSessions = await this.getAllSessions();
    return allSessions.slice(0, limit);
  }

  /** Update session metadata with optimistic update and backend sync. Delegates to SessionCrudService. */
  async updateSession(sessionId, updates) {
    return this._sessionCrudService.updateSession(sessionId, updates);
  }

  /** Mark mutation flag (prevents background refresh spam) */
  _markRecentMutation() {
    this._recentMutation = true;
    setTimeout(() => (this._recentMutation = false), 5000);
  }

  /** Touch session to update lastActivity (local only). Delegates to SessionCrudService. */
  touchSession(sessionId) {
    return this._sessionCrudService.touchSession(sessionId);
  }

  /** Delete session with optimistic update and backend sync. Delegates to SessionCrudService. */
  async deleteSession(sessionId) {
    // Halt this chat's poll loops so they don't keep polling a session that is gone, and
    // keep a poll already in flight hidden, as stopMessage does (the delete can roll back).
    this._stoppedSessions.add(sessionId);
    this._searchCoordinator?.stopForSession(sessionId);
    this._revealCoordinator?.stopForSession(sessionId);
    return this._sessionCrudService.deleteSession(sessionId);
  }

  /** Search sessions using backend full-text search */
  async searchSessions(query) {
    if (!query?.trim()) return [];
    try {
      const { searchAgentSessions } = await import('../../../api/agentSessions');
      const results = await searchAgentSessions(query);
      return results.map((r) => {
        const session = this.sessionsMap.get(r.session_id);
        return {
          sessionId: r.session_id,
          sessionName: session?.title || 'Untitled Session',
          text: r.text,
          source: r.source,
          similarityScore: r.similarity_score,
          userId: r.user_id,
          isArchived: session?.isArchived || false,
          isFavorite: session?.isFavorite || false
        };
      });
    } catch (error) {
      console.error('❌ AGENTCORE: Search failed, using local fallback:', error);
      return this._searchHandler.search(query);
    }
  }

  // ============================================================================
  // EVENT SYSTEM (Public) - Delegates to CallbackManager
  // ============================================================================

  onMessage(callback) {
    return this._callbackManager.onMessage(callback);
  }

  onStateChange(callback) {
    return this._callbackManager.onStateChange(callback);
  }

  onStreamingUpdate(callback) {
    return this._callbackManager.onStreamingUpdate(callback);
  }

  onPhaseChange(callback) {
    return this._callbackManager.onPhaseChange(callback);
  }

  onSessionsRefreshed(callback) {
    return this._callbackManager.onSessionsRefreshed(callback);
  }

  onSearchProgress(callback) {
    return this._callbackManager.onSearchProgress(callback);
  }

  _notifyMessage(sessionId, message) {
    this._callbackManager.notifyMessage(sessionId, message);
  }

  _notifyStateChange(sessionId, newState) {
    this._callbackManager.notifyStateChange(sessionId, newState);
  }

  _notifyError(errorInfo) {
    console.error('🚨 AGENTCORE: Error:', errorInfo);
    this._callbackManager.notifyError(errorInfo);
  }

  _notifyStreamingUpdate(sessionId, message, chunk) {
    this._callbackManager.notifyStreamingUpdate(sessionId, message, chunk);
  }

  _notifySearchProgress(sessionId, event) {
    this._callbackManager.notifySearchProgress(sessionId, event);
  }

  // ============================================================================
  // COMPATIBILITY (Public) - Same interface as SimpleMockAdapter
  // ============================================================================

  getConnectionStatus() {
    return 'connected'; // Could be enhanced to check actual AWS connection
  }

  // Deprecated - adapter is self-contained
  registerPersistedSession() {
    if (import.meta.env.DEV) console.warn(
      '⚠️ AGENTCORE: registerPersistedSession called but not needed - adapter handles own persistence'
    );
  }

  // ============================================================================
  // HIDDEN MESSAGE SUPPORT (for search polling)
  // ============================================================================

  /**
   * TD-2293: Send a hidden control reply (reveal confirm / cancel / poll) on
   * the user's behalf. The user-side message never renders; the agent's
   * response arrives as a normal turn and becomes visible via the existing
   * hidden→visible path (`isInternalOnly: false` at onComplete). Must target
   * the SAME session that produced the quote — the agent harness re-derives
   * the quoted contacts/fields from that session's history.
   *
   * @param {string} sessionId - Session holding the quote/job
   * @param {string} content - Control reply (e.g. `CONFIRM_REVEAL:<token>`)
   */
  async sendControlReply(sessionId, content) {
    // A user-initiated confirm/cancel is a deliberate NEW turn — its response
    // must render. Clear any stop guard left by a previously stopped turn
    // (mirrors sendMessage/retryMessage); otherwise the TD-2383 late-result
    // discard would silently hide the reveal result/ack even though credits
    // were already spent. A background CHECK poll must NOT clear the guard.
    if (
      content?.startsWith?.(ENRICHMENT_REPLY_PREFIX.CONFIRM_REVEAL) ||
      content?.startsWith?.(ENRICHMENT_REPLY_PREFIX.CANCEL_REVEAL)
    ) {
      this._stoppedSessions.delete(sessionId);
    }
    // A manual CHECK_REVEAL_RESULTS (the progress card's fast-finish path)
    // resets the coordinator's pending timer so the scheduled 20s poll can't
    // fire a near-duplicate check → duplicate render turns.
    if (content?.startsWith?.(ENRICHMENT_REPLY_PREFIX.CHECK_REVEAL)) {
      this._revealCoordinator?.notifyManualPoll(sessionId);
    }
    return this._sendHiddenMessage(sessionId, content);
  }

  /**
   * Send a hidden message that won't appear in chat UI.
   * Delegates to HiddenMessageHandler for message creation.
   * Fix #17: Passes userId for authorization check.
   * @param {string} sessionId - Session to send message to
   * @param {string} content - Message content
   */
  async _sendHiddenMessage(sessionId, content, options) {
    const userId = this._getCurrentUserId();
    return this._hiddenMessageHandler.sendHiddenMessage(sessionId, content, userId, options);
  }

  /**
   * Send a search poll and settle with the outcome of its agent turn, so a failed poll
   * reaches the coordinator's retry/backoff path instead of leaving the search active
   * with nothing scheduled.
   * @param {string} sessionId - Session to poll in
   * @param {string} content - CHECK_SEARCH_RESULTS message
   * @throws {Error} If the turn failed or returned neither [BUSY] nor results
   */
  async _sendSearchPoll(sessionId, content) {
    await this._sendHiddenMessage(sessionId, content, { awaitResponse: true });
    const searchId = content.slice(SEARCH_CONFIG.POLL_MESSAGE_PREFIX.length);
    if (this._searchCoordinator?.isPollUnanswered(searchId)) {
      throw new Error(`Search poll got no [BUSY] or results: ${searchId}`);
    }
  }

  _updateMessageInStorage(sessionId, message) {
    const currentMessages = this._messageManager.getForSession(sessionId);
    if (currentMessages.length === 0) return;

    const messageIndex = currentMessages.findIndex((m) => m.id === message.id);
    if (messageIndex !== -1) {
      currentMessages[messageIndex] = message;
      this._messageManager.setForSession(sessionId, currentMessages);
    }
  }

  /**
   * ✅ [TD-1703] FIX #2: Update session ID after AWS response
   * When AWS returns a different session ID than what we sent,
   * update all our internal mappings to use AWS's ID
   * @param {string} oldSessionId - Client-generated session ID
   * @param {string} newSessionId - AWS-returned session ID
   */
  async _updateSessionId(oldSessionId, newSessionId) {
    if (import.meta.env.DEV) console.log('🔄 AGENTCORE: Updating session ID from', oldSessionId, 'to', newSessionId);

    // Get session and messages with old ID
    const session = this._sessionManager.getById(oldSessionId);
    const messages = this._messageManager.getForSession(oldSessionId);

    if (!session) {
      console.error('❌ AGENTCORE: Cannot update session ID - session not found:', oldSessionId);
      return;
    }

    // Update session IDs
    session.id = newSessionId;
    session.runtimeSessionId = newSessionId;
    session._awaitingFirstMessage = false;

    // Remove old entries
    this._sessionManager.deleteById(oldSessionId);
    this._messageManager.deleteForSession(oldSessionId);

    // Add with new ID
    this._sessionManager.set(newSessionId, session);
    this._messageManager.setForSession(newSessionId, messages);

    // Persist changes
    this._sessionManager.save();
    this._messageManager.save();

    if (import.meta.env.DEV) console.log('✅ AGENTCORE: Session ID updated successfully');
  }

  /**
   * ✅ [TD-1703] PREVENTION: Cache validation layer
   * Delegates to shared utility for consistent validation across adapters.
   */
  _validateCache(options = {}) {
    return validateCache(
      this._sessionManager.getMap(),
      this._messageManager.getMap(),
      { ...options, onSaveMessages: () => this._messageManager.save() },
      ADAPTER_NAME
    );
  }

  _handlePhaseManagerUpdate(event) {
    // Handle phase manager updates and propagate to UI
    this._notifyMessage(event.sessionId, event.message);
  }

  _handlePhaseChange(event) {
    // Handle phase changes via CallbackManager
    this._callbackManager.notifyPhaseChange(event);
  }

  // ============================================================================
  // CLEANUP (Public)
  // ============================================================================

  destroy() {
    if (import.meta.env.DEV) console.log('🧹 AGENTCORE: Cleaning up adapter');

    // TD-2347: abort any in-flight streaming turns
    if (this._activeStreams) {
      this._activeStreams.forEach((entry) => entry.abortController?.abort());
      this._activeStreams.clear();
    }
    this._stoppedSessions?.clear(); // TD-2383

    // TD-1845: Clean up pubSub subscription to prevent memory leaks
    if (this._logoutHandler) {
      pubSub.unsubscribe(this._logoutHandler);
      this._logoutHandler = null;
    }

    // Fix #2: Invoke all stored unsubscribe functions (messagePhaseManager, etc.)
    if (this._unsubscribers) {
      this._unsubscribers.forEach((unsub) => {
        if (typeof unsub === 'function') unsub();
      });
      this._unsubscribers = [];
    }

    // Clean up callback manager (replaces manual callback arrays)
    this._callbackManager?.destroy();
    this._callbackManager = null;

    // Clean up search coordinator
    this._searchCoordinator?.destroy();
    this._searchCoordinator = null;

    // TD-2293: clean up the reveal poll coordinator
    this._revealCoordinator?.destroy();
    this._revealCoordinator = null;

    // Clean up response handler
    this._responseHandler?.destroy();
    this._responseHandler = null;

    // Clean up cache sync strategy (has timers and abort controllers)
    this._cacheSyncStrategy?.destroy();
    this._cacheSyncStrategy = null;

    // Stateless services - null references for consistency
    this._backendSync = null;
    this._payloadBuilder = null;
    this._retryHandler = null;
    this._searchHandler = null;
    this._sessionCrudService = null;
    this._hiddenMessageHandler = null;
    this._streamingProcessor = null;

    // AWS client doesn't need explicit cleanup
  }

  // ============================================================================
  // BACKWARD COMPAT GETTERS (for test access - read-only)
  // ============================================================================

  /** @deprecated Use _sessionManager methods directly. For test access only. */
  get sessions() {
    return this._sessionManager.getMap();
  }

  /** @deprecated Use _messageManager methods directly. For test access only. */
  get messages() {
    return this._messageManager.getMap();
  }

  /** @deprecated Use _sessionManager.save() directly. For test access only. */
  saveSessions() {
    this._sessionManager.save();
  }

  /** @deprecated Use _messageManager.save() directly. For test access only. */
  saveMessages() {
    this._messageManager.save();
  }

  /**
   * TD-1880: Expose search coordinator state for AgentSessionProvider.
   * This getter allows the UI layer to capture search metadata (toolName, searchId, startTime)
   * when a search begins, enabling the loading animation to display correctly.
   *
   * Fix #18: Invariants - When active=false, all other properties are null.
   * When active=true, toolName, searchId, startTime, sessionId are non-null.
   *
   * Fix #16: This getter is user-scoped - only returns state for the current user's adapter.
   *
   * @returns {Object} Current search state from SearchPollCoordinator
   * @returns {boolean} return.active - Whether a search is currently active
   * @returns {string|null} return.toolName - Tool name (null when inactive)
   * @returns {string|null} return.searchId - Search ID (null when inactive)
   * @returns {number|null} return.startTime - Timestamp when started (null when inactive)
   * @returns {string|null} return.sessionId - Session ID (null when inactive)
   */
  get currentSearchState() {
    // Fix #1: Check for destroyed/null coordinator and log warning in dev mode
    if (!this._searchCoordinator) {
      if (import.meta.env.DEV) {
        console.warn('⚠️ AGENTCORE: currentSearchState accessed but coordinator is null');
      }
      return this._inactiveSearchState;
    }

    // Fix #7: Check if coordinator is destroyed
    if (this._searchCoordinator.isDestroyed) {
      if (import.meta.env.DEV) {
        console.warn('⚠️ AGENTCORE: currentSearchState accessed but coordinator is destroyed');
      }
      return this._inactiveSearchState;
    }

    // Fix #20: Wrap getState in try-catch to prevent UI crashes
    try {
      const state = this._searchCoordinator.getState();

      // Fix #18: Runtime assertion for invariants
      if (state.active) {
        // When active, verify all required properties are present
        if (!state.toolName || !state.searchId || !state.startTime || !state.sessionId) {
          console.error('❌ AGENTCORE: Active search state missing required properties:', state);
          // Return inactive state to prevent UI issues
          return this._inactiveSearchState;
        }
      }

      return state;
    } catch (error) {
      // Fix #20: Log error and return safe fallback
      console.error('❌ AGENTCORE: Error accessing search coordinator state:', error);
      return this._inactiveSearchState;
    }
  }

  /**
   * TD-2952: session-scoped search state so UI metadata for session A never
   * reads a concurrent search belonging to session B.
   * @param {string} sessionId
   * @returns {Object} Search state for that session (inactive when none)
   */
  getSearchStateForSession(sessionId) {
    if (!this._searchCoordinator || this._searchCoordinator.isDestroyed) {
      return this._inactiveSearchState;
    }
    try {
      const state = this._searchCoordinator.getStateForSession(sessionId);
      if (state.active && (!state.toolName || !state.searchId || !state.startTime || !state.sessionId)) {
        console.error('❌ AGENTCORE: Active search state missing required properties:', state);
        return this._inactiveSearchState;
      }
      return state;
    } catch (error) {
      console.error('❌ AGENTCORE: Error accessing search state for session:', error);
      return this._inactiveSearchState;
    }
  }

  /**
   * Whether any search is still active for the given session.
   * @param {string} sessionId
   * @returns {boolean}
   */
  isSearchActiveForSession(sessionId) {
    return this._searchCoordinator?.isSearchActiveForSession(sessionId) === true;
  }

  /**
   * Fix #6: Test helper method to simulate search start without accessing internal coordinator.
   * @internal For test use only - delegates to coordinator's handleSearchTags.
   * @param {string} toolName - Tool name for the search
   * @param {string} searchId - Unique search identifier
   * @param {string} sessionId - Session context for the search
   */
  _simulateSearchStart(toolName, searchId, sessionId) {
    if (!this._searchCoordinator || this._searchCoordinator.isDestroyed) {
      throw new Error('Cannot simulate search: coordinator not available');
    }
    const tag = `[SEARCH_STARTED:${toolName}:${searchId}]`;
    return this._searchCoordinator.handleSearchTags(tag, sessionId);
  }
}

export default AgentCoreAdapter;
